import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"
import type { DayPickerProps } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

// Cross-compatible nav icons for react-day-picker v8 (IconLeft/IconRight) and v9 (Chevron)
const navComponents: Record<string, any> = {
  IconLeft: () => <ChevronLeft className="h-4 w-4" />,
  IconRight: () => <ChevronRight className="h-4 w-4" />,
  Chevron: ({ orientation, ...rest }: any) => {
    const Icon = orientation === "left" ? ChevronLeft : ChevronRight
    return <Icon className="h-4 w-4" />
  },
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: any) {
  return (
    (<DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("rdp p-2.5", className)}
      classNames={{
        months: "flex w-full flex-col sm:flex-row sm:space-x-4",
        month: "w-full space-y-2",
        caption: "relative mb-2 flex min-h-9 items-center justify-center px-8",
        caption_label: "sr-only",
        caption_dropdowns: "grid w-full grid-cols-[minmax(0,1fr)_112px] gap-2",
        dropdown: "h-9 w-full appearance-none rounded-md border border-slate-300 bg-white px-3 pr-9 text-sm font-semibold text-slate-900 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50",
        dropdown_month: "w-full",
        dropdown_year: "w-full",
        vhidden: "sr-only",
        nav: "pointer-events-none absolute inset-x-0 top-0 flex h-9 items-center justify-between",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "pointer-events-auto h-7 w-7 rounded-md border-slate-300 bg-white p-0 text-slate-600 opacity-100 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-blue-500/25"
        ),
        nav_button_previous: "",
        nav_button_next: "",
        table: "w-full border-collapse",
        head_row: "grid grid-cols-7",
        head_cell:
          "flex h-7 items-center justify-center text-[0.75rem] font-medium text-slate-500",
        row: "flex w-full mt-1",
        cell: cn(
          "relative flex h-8 flex-1 items-center justify-center p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-blue-50 [&:has([aria-selected].day-outside)]:bg-blue-50/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 rounded-md p-0 text-sm font-medium text-slate-800 transition hover:bg-slate-100 hover:text-slate-950 aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-blue-600 text-white shadow-sm hover:bg-blue-600 hover:text-white focus:bg-blue-600 focus:text-white",
        day_today: "bg-slate-100 text-slate-950 ring-1 ring-inset ring-slate-300",
        day_outside:
          "day-outside text-slate-400 opacity-70 aria-selected:bg-blue-50/50 aria-selected:text-slate-400",
        day_disabled: "text-slate-300 opacity-50 hover:bg-transparent hover:text-slate-300",
        day_range_middle:
          "aria-selected:bg-blue-50 aria-selected:text-blue-900",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={navComponents as any}
      {...props} />)
  );
}
Calendar.displayName = "Calendar"

export { Calendar }